/**
 * @file squadup-tracking.js (version 4)
 * @description Manages SquadUp event tracking, abandoned carts, and fetches
 * corresponding WordPress event data via AJAX. Now includes persistence for
 * cart data via localStorage.
 */
jQuery(document).ready(function ($) {

    // --- State Variables ---
    // Initialize currentShoppingCartData by retrieving it from localStorage for persistence.
    // If nothing is found, default to an empty array.
    let currentShoppingCartData = JSON.parse(localStorage.getItem('squadup_abandoned_cart_data')) || [];
    let anonymousId = null;
    let userEmail = getUserEmail();
    
    // A map to store the WordPress event ID for each SquadUp event ID.
    // It is now initialized from localStorage for persistence.
    let wpEventIdMap = JSON.parse(localStorage.getItem('squadup_wpEventIdMap')) || {};

    // A flag to prevent tracking if the data-cart-tracking attribute is false.
    let isTrackingPrevented = false;

    // --- Utility Functions ---

    /**
     * Helper function to get a cookie value by name.
     */
    function getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop().split(';').shift();
        }
        return null;
    }

    /**
     * Helper function to set a cookie.
     */
    function setCookie(name, value) {
        const date = new Date();
        date.setTime(date.getTime() + (30 * 24 * 60 * 60 * 1000));
        const expires = `expires=${date.toUTCString()}`;
        document.cookie = `${name}=${value};${expires};path=/`;
    }

    /**
     * Retrieves or creates a unique anonymous ID.
     * It syncs the ID between localStorage and a cookie for better persistence.
     */
    function getAnonymousId() {
        let id = localStorage.getItem('squadup_abandoned_cart_anonymous_id');
        let cookieId = getCookie('squadup_anonymous_id');

        if (id && !cookieId) {
            setCookie('squadup_anonymous_id', id);
            return id;
        }

        if (!id && cookieId) {
            localStorage.setItem('squadup_abandoned_cart_anonymous_id', cookieId);
            return cookieId;
        }

        if (!id) {
            id = 'anon-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
            localStorage.setItem('squadup_abandoned_cart_anonymous_id', id);
            setCookie('squadup_anonymous_id', id);
        }
        return id;
    }

    /**
     * Attempts to get the user's email from various sources.
     * Prioritizes URL parameters, then input fields, then localStorage.
     */
    function getUserEmail() {
        const urlParams = new URLSearchParams(window.location.search);
        const emailFromUrl = urlParams.get('e');
        if (emailFromUrl) {
            const decodedEmail = decodeURIComponent(emailFromUrl);
            if (decodedEmail.includes('@')) {
                return decodedEmail;
            }
        }

        const emailInput = document.querySelector('.modal input[type="email"][name="email"], .modal input#email_address, input[type="email"][name="email"], input#email_address');
        if (emailInput && emailInput.value && emailInput.value.includes('@')) {
            return emailInput.value;
        }

        const storedEmail = localStorage.getItem('squadup_abandoned_cart_email_tracked_' + anonymousId);
        if (storedEmail && storedEmail.includes('@')) {
            return storedEmail;
        }

        return null;
    }

    /**
     * Debounce function to limit how often a function is called.
     */
    function debounce(func, wait) {
        let timeout;
        return function (...args) {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), wait);
        };
    }

    /**
     * Fetches WordPress event data via an AJAX call.
     * EXPECTS: The backend to return a JSON object with at least 'wp_name', 'wp_price', and 'wp_image_url' keys.
     */
    async function getWordPressEventData(wordpressPostId) {
        if (!wordpressPostId) {
            return {};
        }

        try {
            const response = await $.ajax({
                url: squadup_ajax_object.ajax_url,
                type: 'POST',
                data: {
                    action: 'get_wordpress_event_data',
                    post_id: wordpressPostId,
                    nonce: squadup_ajax_object.nonce,
                },
            });
            // --- DEBUGGING LOG ADDED HERE ---
            console.log(`SquadUp Abandoned Cart: WordPress Data Response for post ID ${wordpressPostId}:`, response.data);
            return response.data;
        } catch (error) {
            console.error(`Error fetching WordPress data for post ID ${wordpressPostId}:`, error);
            return {};
        }
    }

    /**
     * Sends tracking data to the server via AJAX.
     */
    function sendTrackingData(eventType, eventData, userEmail = null) {
        if (typeof squadup_ajax_object === 'undefined' || !squadup_ajax_object.nonce || !squadup_ajax_object.ajax_url) {
            console.error('SquadUp Abandoned Cart: AJAX object is not defined or missing properties.');
            return;
        }

        const dataToSend = {
            action: 'squadup_track_event',
            event_type: eventType,
            event_data: JSON.stringify(eventData),
            user_email: userEmail,
            anonymous_id: anonymousId,
            nonce: squadup_ajax_object.nonce,
        };

        // --- CONSOLE LOG FOR DEBUGGING ---
        console.log(`SquadUp Abandoned Cart: Sending '${eventType}' event.`, {
            event_type: eventType,
            user_email: userEmail,
            event_data_sent_in_ajax: dataToSend.event_data,
            full_ajax_payload: dataToSend
        });

        $.ajax({
            url: squadup_ajax_object.ajax_url,
            type: 'POST',
            data: dataToSend,
            success: function (response) {
                console.log(`SquadUp Abandoned Cart: '${eventType}' event processed successfully.`, response);
                if (userEmail && (eventType === 'emailInput' || eventType === 'checkoutAttempt' || eventType === 'shoppingCartUpdated')) {
                    localStorage.setItem('squadup_abandoned_cart_email_tracked_' + anonymousId, userEmail);
                }
            },
            error: function (jqXHR, textStatus, errorThrown) {
                console.error('SquadUp Abandoned Cart: Error sending tracking data:', textStatus, errorThrown, jqXHR.responseText);
            }
        });
    }

    // --- Event Listeners ---

    /**
     * The button click handler is responsible for populating the wpEventIdMap
     * and saving it to localStorage for every button that's clicked.
     */
    $(document).on('click', '.launch-modal-btn[data-wp-id][data-event-id]', function () {
        const squadupEventId = $(this).data('eventId');
        const wordpressEventId = $(this).data('wpId');

        // Store the mapping for later use.
        if (squadupEventId && wordpressEventId) {
            wpEventIdMap[squadupEventId] = wordpressEventId;
            // Save the map to localStorage for persistence
            localStorage.setItem('squadup_wpEventIdMap', JSON.stringify(wpEventIdMap));
        } else {
            console.warn(`Could not map event ID. squadupEventId: ${squadupEventId}, wordpressEventId: ${wordpressEventId}`);
        }

        isTrackingPrevented = $(this).data('cart-tracking') === false;
        if (isTrackingPrevented) {
             console.log('Cart tracking is prevented for this button click.');
        }
    });


    /**
     * The shopping cart update event listener now fetches WordPress data for each item,
     * saves the cart to localStorage, and sends the final cart data to the server.
     */
    document.addEventListener("shoppingCartUpdated", async function (e) {
        if (isTrackingPrevented) {
            console.log("Skipping cart tracking for this event as tracking was explicitly prevented.");
            isTrackingPrevented = false; // Reset the flag
            return;
        }

        var rawShoppingCartData = e.detail;
        console.log("DEBUG: Raw data from shoppingCartUpdated event:", rawShoppingCartData);
        
        setTimeout(async function () {
            let optimizedShoppingCartData = [];
            const fetchPromises = [];

            if (Array.isArray(rawShoppingCartData) && rawShoppingCartData.length > 0) {
                rawShoppingCartData.forEach(eventItem => {
                    if (eventItem.tickets && Array.isArray(eventItem.tickets)) {
                        eventItem.tickets.forEach(ticketItem => {
                            const priceTier = ticketItem.priceTier;
                            const eventDetails = priceTier && priceTier.event ? priceTier.event : {};
                            
                            const wordpressPostId = wpEventIdMap[eventDetails.id] || null;
                            
                            if (wordpressPostId === null) {
                                console.warn(`WordPress post ID not found in map for event_id: ${eventDetails.id}`);
                            }

                            // Create an object to hold both SquadUp and future WordPress data
                            const itemData = {
                                wordpress_event_id: wordpressPostId,
                                squadup_event_id: eventDetails.id,
                                quantity: ticketItem.quantity,
                                // Original SquadUp data
                                name: ticketItem.name,
                                price: priceTier.price,
                            };

                            // Add the promise to fetch WordPress data
                            if (wordpressPostId) {
                                fetchPromises.push(getWordPressEventData(wordpressPostId).then(wpData => {
                                    // Merge the WordPress data into the itemData object
                                    Object.assign(itemData, wpData);
                                    optimizedShoppingCartData.push(itemData);
                                }));
                            } else {
                                // If no WordPress ID, just add the SquadUp data
                                optimizedShoppingCartData.push(itemData);
                            }
                        });
                    }
                });
            } else {
                optimizedShoppingCartData = [];
            }
            
            // Wait for all WordPress data to be fetched
            await Promise.all(fetchPromises);

            currentShoppingCartData = optimizedShoppingCartData;
            
            // Persist the cart data to localStorage
            localStorage.setItem('squadup_abandoned_cart_data', JSON.stringify(currentShoppingCartData));

            sendTrackingData('shoppingCartUpdated', optimizedShoppingCartData, getUserEmail());
        }, 500);
    }, false);


    document.addEventListener("orderSuccessful", function (e) {
        var orderData = e.detail;
        const cartData = currentShoppingCartData || [];
        sendTrackingData('orderSuccessful', cartData, getUserEmail());
        localStorage.removeItem('squadup_abandoned_cart_anonymous_id');
        localStorage.removeItem('squadup_abandoned_cart_data');
        const newAnonymousId = getAnonymousId();
        localStorage.removeItem('squadup_abandoned_cart_email_tracked_' + newAnonymousId);
    }, false);

    const sendEmailInputData = debounce(function (email) {
        console.log("Email tracking triggered after user finished typing:", email);
        if (email && email.includes('@')) {
            const cartData = currentShoppingCartData || [];
            sendTrackingData('emailInput', cartData, email);
        }
    }, 1000);

    $(document).on('keyup', '.modal input[type="email"], .modal input#email_address, input[type="email"], input[name*="email"]', function () {
        const email = $(this).val();
        if (email && email.includes('@')) {
            sendEmailInputData(email);
        }
    });

    // --- Initialization ---

    // Get a unique anonymous ID for tracking and get the user email if available.
    anonymousId = getAnonymousId();
    userEmail = getUserEmail();

    /**
     * Check if the user is on the order confirmation page and mark the cart as converted.
     * This acts as a fallback if the "orderSuccessful" event is not fired.
     */
    if (squadup_ajax_object.orderConfirmationSlug && window.location.href.includes(squadup_ajax_object.orderConfirmationSlug)) {
        // Retrieve cart data from local storage for the final conversion check
        const storedCartData = JSON.parse(localStorage.getItem('squadup_abandoned_cart_data'));

        if (storedCartData && storedCartData.length > 0) {
            console.log('Order confirmation page detected. Marking cart as converted.');
            // Send a checkout_completed event to the backend.
            sendTrackingData('checkout_completed', storedCartData, getUserEmail());
        } else {
             // If no cart data exists, send a minimal event to still log the conversion.
             sendTrackingData('checkout_completed', [], getUserEmail());
        }
        // Always clear the local storage items to prevent false abandonment tracking, regardless of cart content.
        localStorage.removeItem('squadup_abandoned_cart_anonymous_id');
        localStorage.removeItem('squadup_abandoned_cart_data');
        localStorage.removeItem('squadup_abandoned_cart_email_tracked_' + anonymousId);
    }
});
